## Terms

-company-name = Stiftung Unabhängige Patientenberatung Deutschland
-company-short-name = Stiftung UPD
-channel-phone = Telefon
-channel-phone-regional = Telefon regional
-channel-mail = Mail
-channel-mail-regional = Mail
-channel-chat = Chat
-channel-local = Vor Ort
-channel-postal = Brief
-channel-postal-regional = Brief

## Global components

global-default-document-title = {-app-name} der {-company-name}
global-skip-to-content = Zum Inhalt springen
global-notification-center-label = Benachrichtigungs-Center
global-breadcrumbs-label = Seitenpfad
global-table-of-contents-title = Inhalt dieser Seite
global-floating-support = Support-Ticket

## Actions

global-actions = Aktionen
global-forgot-password = Passwort vergessen
global-save = Änderungen speichern
global-cancel = Abbrechen
global-dialog-close = Schließen
global-reveal-password = Passwort aufdecken
global-print = Drucken
global-help = Hilfe
global-help-for = Hilfe zu „{ $topic }“
global-filter = Filter
global-filter-reset = Filter zurücksetzen
global-remove = entfernen
global-unrecoverable-action = Dieser Vorgang kann nicht rückgängig gemacht werden.
global-link-opens-in-new-tab = Öffnet in neuem Tab
global-logout = Abmelden

## Status

global-status-message = Benachrichtigung
global-status-message-close = Benachrichtigung schließen

## Date and time

global-na = Keine Angabe
global-number = { $number }
global-date = { DATETIME($date, year: "numeric", month: "short", day: "numeric") }
global-time = { DATETIME($date, timeStyle: "short") }
global-date-time = { DATETIME($date, dateStyle: "long", timeStyle: "short") }
global-language = { DISPLAY_NAME($locale, type: "language") }
global-time-span = { DATETIME($start, timeStyle: "short") } – { DATETIME($end, timeStyle: "short") }
global-day-time-span = { CAPITALIZE(RELATIVE_DATE($start)) },
  { DATETIME($start, timeStyle: "short") } –
  { DATETIME($end, timeStyle: "short") } Uhr
global-relative-date-time = { RELATIVE_DATE_TIME($date) }
global-time-duration = { TIME_DURATION($start, $end) }
global-date-full = { DATETIME($date, dateStyle: "full") }

## Data

global-loading = Lade Daten…
global-empty = Keine Daten
global-no-results = Keine Ergebnisse
global-file-size = { FILE_SIZE($size) }

## Errors

global-error = Fehler: {$message}
global-error-message =
  { $code ->
    *[x_unknown] [{$method} {$code}]
    [unknown] Es ist ein unbekannter Fehler aufgetreten.
    [unknown_method] Die Anfrage-Methode wird nicht unterstützt.
    [validation] Es sind Validierungs-Fehler aufgetreten.
    [unauthorized] Ihre Authentisierungs-Details sind momentan nicht gültig.
    [forbidden] Keine Berechtigung für diese Aktion.
    [not_found] Die Seite konnte nicht gefunden werden.
    [internal] Ein interner Server-Fehler ist aufgetreten.
    [password_reset_link_expired] Der Link zum Zurücksetzen des Passworts ist nicht mehr gültig.
    [activation_link_expired] Der Link zur Aktivierung Ihres Benutzer:innen-Kontos ist nicht mehr gültig.
    [request_timeout] Der Upload hat zu lange gedauert und wurde abgebrochen.
    [content_too_large] Die Dateien sind zu groß. Die maximale Dateigröße ist {FILE_SIZE(20971520)}.
    [bad_gateway] Es konnte keine Verbindung zum {-app-name} aufgebaut werden.
    [service_unavailable] Es konnte keine Verbindung zum {-app-name} aufgebaut werden.
    [gateway_timeout] Es konnte keine Verbindung zum {-app-name} aufgebaut werden.
    [general_request_error] Die Anfrage konnte leider nicht erstellt werden. Bitte versuchen Sie es später erneut.
  }
global-field-error-message =
  { $code ->
    *[x_unknown] [{$code}]
    [unknown] Ein unbekannter Fehler ist aufgetreten.
    [blocked] Dieser Account ist permanent blockiert.
    [flood_blocked] Die Anmelde-Daten wurden wiederholt falsch eingegeben und sämtliche Anmeldeversuche für diesen Account werden nun blockiert. Die Anmeldesperre muss manuell aufgehoben werden.
    [forbidden] Keine Berechtigung für diese Aktion.
    [incomplete_address] Die eingegebene Adresse ist unvollständig.
    [invalid_value] Ungültiger Wert.
    [invalid_grant] Die eingegebenen Anmelde-Daten sind nicht gültig.
    [invalid_client] Die angegebene OAuth-Client-ID ist nicht korrekt.
    [invalid_email] Die angegebene E-Mail-Adresse hat kein gültiges Format.
    [invalid_enum] Dieses Feld muss einen der folgenden Werte haben: {LIST($valid_values, type: "conjunction")}.
    [invalid_type] Ungültiger Dateityp.
    [max_length] Dieses Feld darf höchstens eine Länge von {$max_length} haben.
    [min_length] Dieses Feld muss mindestens eine Länge von {$min_length} haben.
    [password_mismatch] Die eingegebenen Passwörter stimmen nicht überein.
    [password_policy] Das eingegebene Passwort erfüllt nicht die Passwort-Regeln: Das Passwort muss mindestens eine Länge von {$min_length} haben.
    [required] Dieses Feld ist erforderlich.
    [terms_not_accepted] Die Allgemeinen Geschäfts-Bedingungen müssen akzeptiert werden.
    [unique] Dieses Feld muss einzigartig sein.
    [single_admin] Bitte füge eine:n weitere:n Gruppen-Administrator:in hinzu.
    [internal] { $msg ->
      *[x_unknown] [{$code} {$msg}]
      [invalid_slots] Schichtraster ist ungültig, bitte überprüfen.
      [check_weekends] Pflege das Raster für Samstag, Sonntag und Feiertage oder aktiviere alle Tage.
    }
  }
